import React from "react";
import {
  Box,
  Button,
  Chip,
  Grid,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { FamilyType } from "@/features/Login/Family/types.ts";
import { useLeaveFamily } from "@/hooks/familyHooks";
import { useNavigate } from "@tanstack/react-router";
import { useAuthContext } from "@/providers/AuthProvider";
import { ExitToApp as ExitIcon } from "@mui/icons-material";
import AvatarDisplay from "@/features/Login/Profile/AvatarDisplay.tsx";

interface FamilyHeaderProps {
  family: FamilyType;
}

const FamilyHeader: React.FC<FamilyHeaderProps> = ({ family }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const leaveFamilyMutation = useLeaveFamily();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuthContext();
  const isOwner = user?._id === family.owner._id;

  const handleLeaveFamily = async () => {
    if (!confirm("Êtes-vous sûr de vouloir quitter cette famille ?")) return;

    try {
      await leaveFamilyMutation.mutateAsync();
      refreshUser();
      navigate({ to: "/family" });
    } catch (error) {
      console.error("Erreur lors de la tentative de quitter la famille", error);
    }
  };

  return (
    <Paper
      elevation={4}
      sx={{
        p: { xs: 2, md: 3 },
        borderRadius: 2,
        background:
          theme.palette.mode === "dark"
            ? `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`
            : `linear-gradient(45deg, ${theme.palette.primary.light}, ${theme.palette.secondary.light})`,
      }}
    >
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={8}>
          <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
            <Typography
              variant={isMobile ? "h5" : "h4"}
              component="h1"
              fontWeight="bold"
              color="white"
              sx={{
                textShadow: "1px 1px 3px rgba(0,0,0,0.3)",
                mr: 2,
              }}
            >
              {family.name}
            </Typography>

            {isOwner ? (
              <Chip
                label="Propriétaire"
                color="secondary"
                size={isMobile ? "small" : "medium"}
                sx={{ fontWeight: "bold" }}
              />
            ) : (
              <Chip
                label="Membre"
                color="primary"
                variant="outlined"
                size={isMobile ? "small" : "medium"}
                sx={{ fontWeight: "bold", bgcolor: "rgba(255,255,255,0.7)" }}
              />
            )}
          </Box>

          <Box display="flex" alignItems="center" mt={1}>
            <Typography
              variant="subtitle1"
              color="white"
              sx={{
                opacity: 0.9,
                mr: 1,
                textShadow: "1px 1px 2px rgba(0,0,0,0.2)",
              }}
            >
              Créée par:
            </Typography>
            <Box display="flex" alignItems="center">
              <AvatarDisplay
                avatarUri={family.owner.avatar}
                size={24}
                alt={family.owner.username}
                fallbackText={family.owner.username}
              />
              <Typography
                variant="subtitle1"
                color="white"
                sx={{
                  ml: 1,
                  fontWeight: "medium",
                  textShadow: "1px 1px 2px rgba(0,0,0,0.2)",
                }}
              >
                {family.owner.username}
              </Typography>
            </Box>
          </Box>

          <Typography
            variant="body2"
            color="white"
            sx={{
              mt: 1,
              opacity: 0.8,
              textShadow: "1px 1px 2px rgba(0,0,0,0.2)",
            }}
          >
            {family.members.length} membre{family.members.length > 1 ? "s" : ""}{" "}
            • {family.houses.length} maison{family.houses.length > 1 ? "s" : ""}
          </Typography>
        </Grid>

        <Grid item xs={12} md={4}>
          <Box
            display="flex"
            justifyContent={{ xs: "flex-start", md: "flex-end" }}
            mt={{ xs: 2, md: 0 }}
          >
            {!isOwner && (
              <Button
                variant="contained"
                color="error"
                startIcon={<ExitIcon />}
                onClick={handleLeaveFamily}
                disabled={leaveFamilyMutation.isPending}
                sx={{
                  boxShadow: theme.shadows[5],
                  "&:hover": {
                    boxShadow: theme.shadows[8],
                  },
                }}
              >
                {leaveFamilyMutation.isPending
                  ? "Traitement..."
                  : "Quitter la famille"}
              </Button>
            )}
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default FamilyHeader;
